<template>
    <DrawerAccordion id="contact" :title="$t.__(titleLabel)" coloredIcon="user-circle">
        <template #action>
            <CompassPlainButton
                v-if="showEditButton && !isEmpty"
                :label="$t.__('common.actions.edit')"
                :size="CompassEnums.PlainButtonSize.SMALL"
                :variant="CompassEnums.PlainButtonVariantType.NEUTRAL"
                @click.stop="handleEdit"
            />
        </template>
        <div class="overview-contact-block">
            <div class="overview-contact-block__content">
                <div v-if="isEmpty" class="overview-contact-block__empty">
                    <CompassObject size="small" object="contact" />
                    <p
                        class="overview-contact-block__empty-description typographies-paragraph-medium-regular"
                    >
                        {{ $t.__(emptyDescription) }}
                    </p>
                    <CompassTooltip
                        :content="$t.__('overview.contactBlock.empty.disabledTooltip')"
                        :disabled="!disableButton"
                    >
                        <CompassButton
                            type="secondary"
                            size="small"
                            :label="$t.__(emptyButton)"
                            class="overview-contact-block__empty-button"
                            :disabled="disableButton"
                            @click="handleCreateContact"
                        />
                    </CompassTooltip>
                </div>
                <div v-else>
                    <div class="overview-contact-block__content-header">
                        <CompassTooltip
                            :content="$t.__('common.actions.see_the_file')"
                            :arrow="false"
                            placement="right-start"
                        >
                            <RouterLink :to="contact.link">
                                <CompassObject
                                    :label="contact.fullName"
                                    :caption="contact.position"
                                    size="small"
                                    object="contact"
                                    :isLink="true"
                                />
                            </RouterLink>
                        </CompassTooltip>
                    </div>
                    <CompassDescriptionList variant="frozen-field" size="small">
                        <CompassDescriptionListItem
                            v-if="contact.email"
                            :label="$t.__('common.labels.email')"
                        >
                            <EmailLinkCompass
                                :email="contact.email"
                                :linkedid="linkedid"
                                :linkedtype="linkedtype"
                            >
                                {{ contact.email }}
                            </EmailLinkCompass>
                        </CompassDescriptionListItem>
                        <CompassDescriptionListItem
                            v-if="contact.phone"
                            :label="$t.__('common.labels.phone')"
                        >
                            <PhoneLinkCompass :phoneNumber="contact.phone" />
                        </CompassDescriptionListItem>
                        <CompassDescriptionListItem
                            v-if="contact.mobile"
                            :label="$t.__('common.labels.mobile')"
                        >
                            <PhoneLinkCompass :phoneNumber="contact.mobile" />
                        </CompassDescriptionListItem>
                    </CompassDescriptionList>
                </div>
            </div>
            <CompassPlainButton
                v-if="showAllContacts"
                class="overview-contact-block__all-contacts"
                :label="$t.__(allContactsLabel)"
                @click="navigate"
            />
        </div>
    </DrawerAccordion>
</template>
<script>
import EmailLinkCompass from 'vcomponents/overview/tabs/data/EmailLinkCompass.vue';
import PhoneLinkCompass from 'vcomponents/overview/tabs/data/PhoneLinkCompass.vue';
import { arrayToObject } from 'Helpers/arrayToObjectHelper.js';
import DrawerAccordion from 'vcomponents/common/drawer/accordion/DrawerAccordion.vue';
import { createNamespacedHelpers } from 'vuex';

const { mapGetters } = createNamespacedHelpers('user');
const popupPanelMapActions = createNamespacedHelpers('popupPanel').mapActions;

export default {
    components: {
        EmailLinkCompass,
        PhoneLinkCompass,
        DrawerAccordion,
    },
    props: {
        linkedtype: {
            type: String,
            required: true,
        },
        linkedid: {
            type: Number,
            required: true,
        },
    },
    computed: {
        ...mapGetters({
            permissions: 'getPermissions',
        }),
        isOpportunity() {
            return this.linkedtype === 'opportunity';
        },
        titleLabel() {
            return this.isOpportunity
                ? 'overview.contactBlock.opportunity.title'
                : 'overview.contactBlock.third.title';
        },
        isEmpty() {
            return Object.keys(this.contact).length === 0;
        },
        contact() {
            if (this.linkedtype === 'third') {
                return this.$store.getters.mainContact ?? {};
            }
            if (this.isOpportunity) {
                return arrayToObject(this.$store.getters.mainContact);
            }
            return {};
        },
        showEditButton() {
            return this.isOpportunity
                ? this.permissions.acl.update_opportunities
                : this.permissions.acl.update_contacts;
        },
        emptyDescription() {
            return this.isOpportunity
                ? 'overview.contactBlock.empty.opportunity.description'
                : 'overview.contactBlock.empty.third.description';
        },
        emptyButton() {
            return this.isOpportunity
                ? 'overview.contactBlock.empty.opportunity.button'
                : 'overview.contactBlock.empty.third.button';
        },
        disableButton() {
            return this.isOpportunity ? !this.permissions.acl.update_opportunities : false;
        },
        showAllContacts() {
            if (this.isOpportunity) {
                return this.$store.state.opportunity.mainContact.length > 1;
            }
            const companyInfo = this.$store.state.third.dataFetch.informations;
            return companyInfo.find((item) => item.key === 'contactsCount')?.value > 1;
        },
        allContactsLabel() {
            return this.isOpportunity
                ? 'overview.contactBlock.opportunity.allContacts'
                : 'overview.contactBlock.third.allContacts';
        },
    },
    methods: {
        ...popupPanelMapActions(['open']),
        navigate() {
            const route = this.isOpportunity
                ? 'opportunity.overview.informations'
                : 'third.overview.contacts';
            this.$router.push({
                name: route,
                query: this.$route.query,
            });
        },
        handleEdit() {
            const context = this.isOpportunity ? 'opportunityOverview' : 'thirdOverview';
            People.edit(context, this.contact.peopleId);
        },
        handleCreateContact() {
            if (this.isOpportunity) {
                this.open({
                    key: 'opportunityForm',
                    props: {
                        oppId: this.linkedid,
                        emitEditSuccess: 'opportunity:update',
                        context: 'overview',
                    },
                });
            } else {
                People.linkTo('third', { id: this.linkedid });
            }
        },
    },
};
</script>
<style scoped lang="scss">
.overview-contact-block {
    &__empty {
        align-items: center;
        display: flex;
        flex-direction: column;
        padding: var(--spacing-0);

        &-description {
            margin: var(--spacing-1) 0;
        }
    }

    &__content {
        &-header {
            margin: 0 0 var(--spacing-1);
        }

        :deep() {
            .compass-object,
            .compass-object__text {
                min-width: 0;
            }

            .compass-object__caption,
            .compass-object__label {
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .compass-object__label {
                white-space: nowrap;
            }

            .shareUrlBtn {
                font-weight: var(--font-weights-lato-1);
            }

            .compass-icon-button {
                flex-shrink: 0;
            }

            .compass-object--small .compass-object__label {
                line-height: var(--line-heights-20);
            }

            .compass-object--small .compass-object__caption {
                line-height: var(--line-heights-16);
            }

            .compass-description-list__item {
                min-width: 0; // stops overflow
            }

            .compass-description-list__item__content {
                min-height: 20px;
            }

            .copy-element {
                display: block;
                max-width: 100%;
            }

            .compass-link {
                max-width: calc(100% - 24px);

                &__label {
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    max-width: 100%;
                }
            }
        }
    }

    &__empty {
        align-items: center;
        display: flex;
        flex-direction: column;
        justify-content: center;
        padding: var(--spacing-1);

        &-description {
            margin: var(--spacing-1) 0;
        }
    }

    &__all-contacts {
        display: inline-block;
        margin-top: var(--spacing-1);

        :deep() {
            &.compass-plain-button {
                color: var(--colors-common-text-subtle-default, #565665);

                &:hover {
                    color: var(--colors-common-text-subtle-hover, #111111);
                }
            }
        }
    }
}
</style>
